
import React, { useState, useEffect, useRef } from 'react';

interface LoginModalProps {
  onLogin: (role: 'family' | 'staff', id?: string) => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ onLogin }) => {
  const [view, setView] = useState<'initial' | 'staff'>('initial');
  const [input, setInput] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (view === 'staff') {
      inputRef.current?.focus();
    } else {
      setInput('');
      setPassword('');
      setError('');
      setIsButtonEnabled(false);
    }
  }, [view]);

  useEffect(() => {
    if (view === 'staff') {
      setIsButtonEnabled(input.trim() !== '' && password.trim() !== '');
    }
  }, [input, password, view]);

  const handleStaffSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate Employee ID format
    const num = Number(input);
    if (input.trim() === '' || !Number.isInteger(num) || num < 1 || num > 9999 || input.length > 4) {
      setError('Please enter a valid 1-4 digit employee number.');
      return;
    }

    // Validate password
    if (password !== '112233') {
      setError('Incorrect employee number or password.');
      setPassword(''); // Clear password for security
      return;
    }
    
    onLogin('staff', input);
  };

  const handleFamilyLogin = () => {
    onLogin('family');
  };

  const renderInitialView = () => (
    <>
      <div className="space-y-1">
        <h1 className="text-2xl font-bold text-primary">Welcome to Care Guardian</h1>
        <p className="text-gray-500">How would you like to log in?</p>
      </div>
      <div className="space-y-4 pt-4">
        <button
          onClick={handleFamilyLogin}
          className="w-full px-4 py-3 font-bold text-white bg-accent rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-dark transition"
        >
          Login as Family Member
        </button>
        <button
          onClick={() => setView('staff')}
          className="w-full font-semibold text-primary hover:text-purple-700"
        >
          Login as Care Concierge Staff
        </button>
      </div>
    </>
  );
  
  const renderStaffView = () => (
    <>
       <div className="space-y-1">
          <h1 className="text-2xl font-bold text-primary">Staff Login</h1>
          <p className="text-gray-500">Enter your credentials to continue.</p>
        </div>
        <form onSubmit={handleStaffSubmit} className="space-y-4">
          <div>
            <label htmlFor="employee-number" className="sr-only">Employee Number</label>
            <input
              ref={inputRef}
              id="employee-number"
              name="employee-number"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              value={input}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                    setInput(value);
                }
              }}
              placeholder="Employee Number"
              className="w-full px-4 py-3 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-primary transition"
            />
          </div>
          <div>
            <label htmlFor="password" className="sr-only">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                    setPassword(value);
                }
              }}
              placeholder="Password"
              className="w-full px-4 py-3 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-primary transition"
            />
          </div>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={!isButtonEnabled}
            className="w-full px-4 py-3 font-bold text-white bg-accent rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Login
          </button>
           <button
             type="button"
             onClick={() => setView('initial')}
             className="w-full mt-2 font-semibold text-sm text-gray-500 hover:text-primary"
           >
             Back
           </button>
        </form>
    </>
  );

  return (
    <div className="flex items-center justify-center h-full bg-light-bg">
      <div className="w-full max-w-sm p-8 space-y-6 bg-white rounded-2xl shadow-lg text-center">
        <img 
          src="https://bin.mycareconcierge.com/email/careconcierge_logo.png" 
          alt="Care Concierge Logo" 
          className="h-12 mx-auto"
        />
        {view === 'initial' ? renderInitialView() : renderStaffView()}
      </div>
    </div>
  );
};

export default LoginModal;